import ApplicationController from "./application_controller"

export default class extends ApplicationController {
  static targets = [
    "searchInput",
    "regionFilter", 
    "searchButton",
    "locationList",
    "locationContent",
    "loadingState",
    "infiniteLoadingState",
    "infiniteScrollTrigger",
    "noResultsState",
    "mapContainer",
    // Mobile targets
    "mobileLocationList",
    "mobileLocationContent",
    "mobileLoadingState",
    "mobileInfiniteLoadingState",
    "mobileInfiniteScrollTrigger",
    "mobileNoResultsState"
  ]
  
  static values = {
    csvFileId: Number,
    mapCenterLat: Number,
    mapCenterLng: Number,
    mapZoom: Number,
    mapHighlightColor: String,
    buttonColor: String,
    buttonTextColor: String
  }

  connect() {
    this.currentOffset = 0
    this.pageSize = 50
    this.allLocations = []
    this.currentLocations = []
    this.isLoading = false
    this.hasMore = true
    this.currentSearch = ''
    this.currentRegion = ''
    this.map = null
    this.markers = []
    this.infoWindow = null
    this.intersectionObserver = null
    
    // Debounced search function
    this.debouncedSearch = this.debounce(this.performSearch.bind(this), 300)
    // Debounced load more function
    this.debouncedLoadMore = this.debounce(this.loadMoreLocations.bind(this), 500)
    
    this.initializeData()
  }

  disconnect() {
    if (this.map) {
      // Clean up map resources
      this.clearMarkers()
      this.map = null
    }
    
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
      this.intersectionObserver = null
    }
    
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
      this.resizeHandler = null
    }
  }

  async initializeData() {
    try {
      this.showLoadingState()
      
      // Load initial locations and regions in parallel
      const [locationsResponse, regionsResponse, mapLocationsResponse] = await Promise.all([
        this.loadLocations(),
        this.loadRegions(),
        this.loadAllLocationsForMap()
      ])

      if (locationsResponse.ok) {
        const data = await locationsResponse.json()
        this.currentLocations = data.locations
        this.hasMore = data.has_more
        this.renderLocationList()
      }

      if (regionsResponse.ok) {
        const regionsData = await regionsResponse.json()
        this.populateRegionFilter(regionsData.regions)
      }

      if (mapLocationsResponse.ok) {
        const mapData = await mapLocationsResponse.json()
        this.allLocations = mapData.locations
        await this.initializeMap()
      }

      this.hideLoadingState()
      
    } catch (error) {
      console.error('Failed to initialize dealer locator:', error)
      this.showError('Failed to load dealer locations. Please try again.')
      this.hideLoadingState()
    }
  }

  async loadLocations(offset = 0, search = '', region = '') {
    const params = new URLSearchParams({
      csv_file_id: this.csvFileIdValue,
      offset: offset,
      limit: this.pageSize
    })

    if (search) params.append('search', search)
    if (region) params.append('region', region)

    return fetch(`${dealerLocatorConfig.rest_url}locations/?${params}`, {
      headers: {
        'X-WP-Nonce': dealerLocatorConfig.nonce
      }
    })
  }

  async loadAllLocationsForMap() {
    const params = new URLSearchParams({
      csv_file_id: this.csvFileIdValue
    })

    return fetch(`${dealerLocatorConfig.rest_url}all-locations/?${params}`, {
      headers: {
        'X-WP-Nonce': dealerLocatorConfig.nonce
      }
    })
  }

  async loadRegions() {
    const params = new URLSearchParams({
      csv_file_id: this.csvFileIdValue
    })

    return fetch(`${dealerLocatorConfig.rest_url}regions/?${params}`, {
      headers: {
        'X-WP-Nonce': dealerLocatorConfig.nonce
      }
    })
  }

  populateRegionFilter(regions) {
    // Clear existing options (except "All regions")
    const select = this.regionFilterTarget
    const firstOption = select.firstElementChild
    select.innerHTML = ''
    select.appendChild(firstOption)

    // Add region options
    regions.forEach(region => {
      const option = document.createElement('option')
      option.value = region
      option.textContent = region
      select.appendChild(option)
    })
  }

  async initializeMap() {
    // Wait for Google Maps API to load (max 10 seconds)
    const maxWait = 10000
    const interval = 100
    let waited = 0

    while ((!window.google || !window.google.maps) && waited < maxWait) {
      await new Promise(resolve => setTimeout(resolve, interval))
      waited += interval
    }

    if (!window.google || !window.google.maps) {
      console.error('Google Maps API failed to load after ' + maxWait + 'ms')
      this.showError('Failed to load map. Please refresh the page.')
      return
    }

    const mapOptions = {
      center: {
        lat: this.mapCenterLatValue,
        lng: this.mapCenterLngValue
      },
      zoom: this.mapZoomValue,
      styles: this.getMapStyles(),
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      // Add padding to avoid markers behind search overlay
      padding: this.getMapPadding()
    }

    this.map = new google.maps.Map(this.mapContainerTarget, mapOptions)
    this.infoWindow = new google.maps.InfoWindow()

    // Add resize handler for responsive padding
    this.resizeHandler = () => {
      if (this.map) {
        this.map.setOptions({ padding: this.getMapPadding() })
      }
    }
    window.addEventListener('resize', this.resizeHandler)

    // Add markers for all locations
    this.addMarkersToMap(this.allLocations)

    // Fit map to markers if we have locations
    if (this.allLocations.length > 0) {
      this.fitMapToMarkers()
    }
  }

  addMarkersToMap(locations) {
    this.clearMarkers()

    locations.forEach((location) => {
      const marker = new google.maps.Marker({
        position: {
          lat: parseFloat(location.latitude),
          lng: parseFloat(location.longitude)
        },
        map: this.map,
        title: location.name,
        icon: this.getCustomMarkerIcon(),
        animation: google.maps.Animation.DROP
      })

      // Add click listener for info window
      marker.addListener('click', () => {
        this.showInfoWindow(marker, location)
      })

      this.markers.push(marker)
    })
  }

  clearMarkers() {
    this.markers.forEach(marker => {
      marker.setMap(null)
    })
    this.markers = []
  }

  showInfoWindow(marker, location) {
    const content = this.createInfoWindowContent(location)
    this.infoWindow.setContent(content)
    
    // First center the marker, then offset down to make room for search overlay
    this.map.panTo(marker.getPosition())
    
    // Pan the map up by pixels to make the marker appear lower (making room for search overlay)
    setTimeout(() => {
      this.map.panBy(0, -120) // Negative Y moves map up, making marker appear lower
    }, 1)
    
    this.infoWindow.open(this.map, marker)
  }

  createInfoWindowContent(location) {
    const imageHtml = location.image_url ? 
      `<div class="w-full mb-6">
         <img src="${this.escapeAttribute(location.image_url)}" alt="${this.escapeAttribute(location.name)}" class="w-full h-auto rounded-sm" onerror="this.style.display='none'">
       </div>` : ''
       
    const buttonsHtml = []
    
    if (location.website_url) {
      buttonsHtml.push(`
        <a href="${this.escapeAttribute(location.website_url)}" target="_blank" 
           class="flex items-center justify-center gap-2 px-3 py-2 text-xs font-semibold rounded-md transition-opacity flex-1 hover:opacity-70"
           style="background-color: ${this.buttonColorValue}; color: ${this.buttonTextColorValue};">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
          </svg>
          Website
        </a>
      `)
    }
    
    if (location.phone) {
      buttonsHtml.push(`
        <a href="tel:${this.escapeAttribute(location.phone)}" 
           class="flex items-center justify-center gap-2 px-3 py-2 border text-xs font-semibold rounded-md transition-opacity flex-1 hover:opacity-70"
           style="border-color: ${this.buttonColorValue}; color: ${this.buttonTextColorValue}; background-color: transparent;">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
          </svg>
          Call
        </a>
      `)
    }
    
    return `
      <div style="padding: 12px; padding-top:0; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); border-radius: 4px; max-width: 280px;">
        ${imageHtml}
        <h3 class="text-2xl font-semibold font-display mb-1 text-gray-900 -mt-2">${this.escapeTextContent(location.name)}</h3>
        <p class="text-sm text-gray-900 mb-1">Location: <span class="font-semibold">${this.escapeTextContent(location.region_state)}</span></p>
        ${location.description ? `<p class="text-sm text-gray-900 mb-3">${this.escapeTextContent(location.description)}</p>` : ''}
        ${buttonsHtml.length > 0 ? `<div class="flex gap-2 mt-4">${buttonsHtml.join('')}</div>` : ''}
      </div>
    `
  }


  getCustomMarkerIcon() {
    const highlightColor = this.mapHighlightColorValue || '#eab308'
    
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
        <svg width="32" height="40" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 0C7.164 0 0 7.164 0 16s16 24 16 24 16-15.164 16-24S24.836 0 16 0zm0 24c-4.418 0-8-3.582-8-8s3.582-8 8-8 8 3.582 8 8-3.582 8-8 8z" fill="${highlightColor}"/>
          <circle cx="16" cy="16" r="6" fill="#000"/>
        </svg>
      `)}`,
      scaledSize: new google.maps.Size(32, 40),
      anchor: new google.maps.Point(16, 40)
    }
  }

  getMapPadding() {
    // Responsive padding based on screen size
    const isMobile = window.innerWidth < 1024 // lg breakpoint
    
    return {
      top: isMobile ? 100 : 120, // Smaller overlay on mobile
      right: 16,
      bottom: 16,
      left: 16
    }
  }

  getMapStyles() {
    // Custom map style for 4x4 Australia branding
    return [
      {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "off" }]
      },
      {
        featureType: "transit",
        elementType: "labels",
        stylers: [{ visibility: "off" }]
      }
    ]
  }

  fitMapToMarkers() {
    if (this.markers.length === 0) return

    const bounds = new google.maps.LatLngBounds()
    this.markers.forEach(marker => {
      bounds.extend(marker.getPosition())
    })
    
    this.map.fitBounds(bounds)
    
    // Ensure minimum zoom level
    google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
      if (this.map.getZoom() > 15) {
        this.map.setZoom(15)
      }
    })
  }

  renderLocationList(append = false) {
    const targets = this.getCurrentTargets()
    const container = targets.locationContent
    
    if (!append) {
      // Clear content but preserve loading indicator and scroll trigger
      const loadingIndicator = targets.infiniteLoadingState
      const trigger = targets.infiniteScrollTrigger
      container.innerHTML = ''
      container.appendChild(loadingIndicator)
      container.appendChild(trigger)
    }

    this.currentLocations.forEach(location => {
      const locationElement = this.createLocationElement(location)
      // Insert before the loading indicator
      container.insertBefore(locationElement, targets.infiniteLoadingState)
    })

    // Setup infinite scroll observer after first render
    if (!append && this.hasMore) {
      this.setupInfiniteScroll()
    }

    // Show/hide no results state
    if (this.currentLocations.length === 0) {
      targets.noResultsState.classList.remove('hidden')
    } else {
      targets.noResultsState.classList.add('hidden')
    }
  }

  createLocationElement(location) {
    const div = document.createElement('div')
    div.className = 'hub-dealer-locator-section__location-item group cursor-pointer overflow-hidden m-0 py-4 hover:opacity-70 transition-opacity [&:not(:first-child)]:border-t [&:not(:first-child)]:border-gray-300'
    
    const imageHtml = location.image_url ? 
      `<div class="w-24 h-20 flex-shrink-0">
         <img src="${this.escapeAttribute(location.image_url)}" alt="${this.escapeAttribute(location.name)}" class="w-full h-full object-cover" onerror="this.style.display='none'">
       </div>` : 
      `<div class="w-24 h-20 bg-gray-200 flex-shrink-0 flex items-center justify-center">
         <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
           <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
         </svg>
       </div>`
    
    div.innerHTML = `
      <div class="flex items-center gap-4">
        ${imageHtml}
        
        <div class="flex-1 min-w-0">
          <h4 class="text-2xl font-semibold font-display mb-1 text-gray-900 -mt-2">${this.escapeTextContent(location.name)}</h4>
          <p class="text-xs text-gray-900 mb-1">Location: <span class="font-semibold">${this.escapeTextContent(location.region_state)}</span></p>
          ${location.description ? `<p class="text-xs text-gray-900 line-clamp-3">${this.escapeTextContent(location.description)}</p>` : ''}
        </div>
        
        <div class="flex-shrink-0">
          <div class="hub-dealer-locator-section__item-button flex items-center justify-center text-center border rounded-md text-xs font-semibold px-3 py-2 leading-tight bg-transparent transition-colors self-start pointer-events-none" style="border-color: ${this.buttonColorValue}; color: ${this.buttonTextColorValue};">
            More Details
          </div>
        </div>
      </div>
    `

    // Add click handler to focus map on this location
    div.addEventListener('click', () => {
      this.focusMapOnLocation(location)
    })

    return div
  }

  focusMapOnLocation(location) {
    if (!this.map) return

    const position = {
      lat: parseFloat(location.latitude),
      lng: parseFloat(location.longitude)
    }

    this.map.setCenter(position)
    this.map.setZoom(15)

    // Find and open info window for this marker
    const marker = this.markers.find(m => 
      Math.abs(m.getPosition().lat() - position.lat) < 0.0001 &&
      Math.abs(m.getPosition().lng() - position.lng) < 0.0001
    )

    if (marker) {
      this.showInfoWindow(marker, location)
    }

    // On mobile, scroll user up to see the map
    this.scrollToMapOnMobile()
  }

  scrollToMapOnMobile() {
    const isMobile = window.innerWidth < 1024 // lg breakpoint
    
    if (isMobile && this.mapContainerTarget) {
      // Smooth scroll to map container
      this.mapContainerTarget.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }
  }

  setupInfiniteScroll() {
    // Clean up existing observer
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
    }

    // Only set up if we have more items to load and locations to display
    if (!this.hasMore || this.currentLocations.length === 0) {
      return
    }

    const targets = this.getCurrentTargets()

    // Create new intersection observer with the scrollable container as root
    this.intersectionObserver = new IntersectionObserver((entries) => {
      const entry = entries[0]
      if (entry.isIntersecting && !this.isLoading && this.hasMore) {
        // Show loading indicator immediately
        this.showInfiniteLoadingState()
        this.debouncedLoadMore()
      }
    }, {
      root: targets.locationList, // Use scrollable container as root
      threshold: 0.1,
      rootMargin: '20px'
    })

    // Start observing the trigger element
    if (targets.infiniteScrollTrigger) {
      this.intersectionObserver.observe(targets.infiniteScrollTrigger)
    }
  }

  // Event handlers
  searchInputTargetConnected() {
    this.searchInputTarget.addEventListener('input', (e) => {
      this.debouncedSearch(e.target.value, this.regionFilterTarget.value)
    })
  }

  regionFilterTargetConnected() {
    this.regionFilterTarget.addEventListener('change', (e) => {
      this.performSearch(this.searchInputTarget.value, e.target.value)
    })
  }

  searchButtonTargetConnected() {
    this.searchButtonTarget.addEventListener('click', () => {
      this.performSearch(this.searchInputTarget.value, this.regionFilterTarget.value)
    })
  }


  async performSearch(searchTerm = '', region = '') {
    if (this.isLoading) return

    this.currentSearch = searchTerm.trim()
    this.currentRegion = region
    this.currentOffset = 0
    this.currentLocations = []

    try {
      this.isLoading = true
      this.showLoadingState()

      const response = await this.loadLocations(0, this.currentSearch, this.currentRegion)
      
      if (response.ok) {
        const data = await response.json()
        this.currentLocations = data.locations
        this.hasMore = data.has_more
        this.currentOffset = this.pageSize
        
        this.renderLocationList()
        
        // Reset scroll position to top after a brief delay to ensure DOM update
        setTimeout(() => {
          const targets = this.getCurrentTargets()
          targets.locationList.scrollTop = 0
        }, 50)
        
        // Update map markers based on search results
        this.updateMapForSearch(data.locations)
      } else {
        throw new Error('Search failed')
      }

    } catch (error) {
      console.error('Search error:', error)
      this.showError('Search failed. Please try again.')
    } finally {
      this.isLoading = false
      this.hideLoadingState()
    }
  }

  updateMapForSearch(locations) {
    if (!this.map) return

    if (locations.length === 0) {
      // No results - clear markers and center on default position
      this.clearMarkers()
      this.map.setCenter({
        lat: this.mapCenterLatValue,
        lng: this.mapCenterLngValue
      })
      this.map.setZoom(this.mapZoomValue)
    } else {
      // Show only matching locations on map
      this.addMarkersToMap(locations)
      this.fitMapToMarkers()
    }
  }

  async loadMoreLocations() {
    if (this.isLoading || !this.hasMore) return

    try {
      this.isLoading = true

      const response = await this.loadLocations(
        this.currentOffset,
        this.currentSearch,
        this.currentRegion
      )

      if (response.ok) {
        const data = await response.json()
        const newLocations = data.locations
        this.currentLocations = [...this.currentLocations, ...newLocations]
        this.hasMore = data.has_more
        this.currentOffset += this.pageSize
        
        // Append new locations to the list (before loading indicator)
        const targets = this.getCurrentTargets()
        newLocations.forEach(location => {
          const locationElement = this.createLocationElement(location)
          targets.locationContent.insertBefore(locationElement, targets.infiniteLoadingState)
        })

        // Re-setup infinite scroll if there are more items
        if (this.hasMore) {
          this.setupInfiniteScroll()
        }
      }

    } catch (error) {
      console.error('Load more error:', error)
      this.showError('Failed to load more locations.')
    } finally {
      this.isLoading = false
      
      // Hide infinite loading state with animation
      if (this.hasInfiniteLoadingStateTarget) {
        this.hideInfiniteLoadingState()
      }
    }
  }

  // Helper methods to get current active targets based on screen size
  getCurrentTargets() {
    const isMobile = window.innerWidth < 1024 // lg breakpoint
    
    if (isMobile && this.hasMobileLocationListTarget) {
      return {
        locationList: this.mobileLocationListTarget,
        locationContent: this.mobileLocationContentTarget,
        loadingState: this.mobileLoadingStateTarget,
        infiniteLoadingState: this.mobileInfiniteLoadingStateTarget,
        infiniteScrollTrigger: this.mobileInfiniteScrollTriggerTarget,
        noResultsState: this.mobileNoResultsStateTarget
      }
    } else {
      return {
        locationList: this.locationListTarget,
        locationContent: this.locationContentTarget,
        loadingState: this.loadingStateTarget,
        infiniteLoadingState: this.infiniteLoadingStateTarget,
        infiniteScrollTrigger: this.infiniteScrollTriggerTarget,
        noResultsState: this.noResultsStateTarget
      }
    }
  }

  // Utility methods
  showLoadingState() {
    const targets = this.getCurrentTargets()
    targets.loadingState.classList.remove('hidden')
    targets.locationList.classList.add('hidden')
    targets.noResultsState.classList.add('hidden')
  }

  hideLoadingState() {
    const targets = this.getCurrentTargets()
    targets.loadingState.classList.add('hidden')
    targets.locationList.classList.remove('hidden')
  }

  showInfiniteLoadingState() {
    const targets = this.getCurrentTargets()
    const indicator = targets.infiniteLoadingState
    indicator.classList.remove('hidden', 'opacity-0')
    indicator.classList.add('opacity-100')
  }

  hideInfiniteLoadingState() {
    const targets = this.getCurrentTargets()
    const indicator = targets.infiniteLoadingState
    indicator.classList.remove('opacity-100')
    indicator.classList.add('opacity-0')
    
    // Hide completely after transition
    setTimeout(() => {
      indicator.classList.add('hidden')
    }, 300)
  }

  showError(message) {
    // Simple error handling - could be enhanced with a proper error state
    console.error(message)
    // Could dispatch custom event for global error handling
  }

  escapeAttribute(text) {
    if (!text) return ''
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  escapeTextContent(text) {
    if (!text) return ''
    // For text content, we want to preserve HTML entities like &amp; as &
    // but still escape dangerous characters like < > " '
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      // Then decode common HTML entities back to their characters for display
      .replace(/&amp;amp;/g, '&amp;') // Handle double-encoded ampersands
      .replace(/&amp;/g, '&') // Convert &amp; back to &
  }

  escapeHtml(text) {
    // Legacy method - redirects to escapeTextContent for backward compatibility
    return this.escapeTextContent(text)
  }

  debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }
}